import { useCallback, useState } from 'react';
import { CollapseAndFade } from '@buoyhealth/common.buoy-layout';
import styled, { css } from 'styled-components';
import Typewriter, { TypewriterClass } from 'typewriter-effect';
import { Paragraph } from './Layout/Embedded';

const Container = styled.div<{ hideCursor: boolean }>`
  .Typewriter {
    ${({ theme }) => theme.typography.variants.secondary.main};

    p {
      margin-top: 10px;
    }
  }

  ${({ hideCursor }) =>
    hideCursor &&
    css`
      .Typewriter__cursor {
        display: none;
      }
    `}
`;

const ResultsParagraph = styled(Paragraph)`
  ${({ theme }) => theme.typography.variants.secondary.main};
  margin-top: 10px;

  &:first-of-type {
    margin-top: 0;
  }
`;

const formatResult = (answer?: string) => {
  if (!answer) {
    return '';
  }
  return answer.replace(/\n\n/g, '\n').replace(/\n/g, '<br /><p></p>');
};

const TypewriterResults = ({
  dx,
  sx,
  results,
  onTypewriterStop,
  shouldTypeResults,
}: {
  dx: string;
  sx: string;
  results: string;
  onTypewriterStop?: () => void;
  /** Whether results should animate in or just appear */
  shouldTypeResults?: boolean;
}) => {
  const [typewriterStopped, setTypewriterStopped] = useState(false);

  const initTypewriter = useCallback((typewriter: TypewriterClass) => {
    typewriter
      .pauseFor(1000)
      .typeString(`Searching for ${dx || sx} treatments...`)
      .pauseFor(4000)
      .typeString("<br /><p></p>Sorry for the delay—I'm still thinking.")
      .pauseFor(2000)
      .typeString(`<br /><p></p>Putting together the final touches...`)

      .start();
  }, []);

  return (
    <Container className="BuoyInterviews__TypewriterResults" hideCursor={typewriterStopped}>
      <CollapseAndFade show={!results.length}>
        <Typewriter
          onInit={initTypewriter}
          options={{
            delay: 18,
            deleteSpeed: 10,
          }}
        />
      </CollapseAndFade>

      {!!results.length &&
        !shouldTypeResults &&
        results.split('\n').map((p) => <ResultsParagraph key={p}>{p}</ResultsParagraph>)}

      {!!results.length && shouldTypeResults && (
        <Typewriter
          onInit={(typewriter) => {
            typewriter
              .typeString(formatResult(results))
              .callFunction(() => {
                setTypewriterStopped(true);
                onTypewriterStop?.();
              })
              .start();
          }}
          options={{
            delay: 18,
          }}
        />
      )}
    </Container>
  );
};

export default TypewriterResults;
